import { useState } from "react";
import { toast } from "sonner";
import { Clock, X, Plus, Trash2, Play, Pause, RotateCcw } from "lucide-react";
import { SPEEDS } from "@/lib/playback";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PlayControls = ({ pb }) => (
  <div className="mt-2 flex items-center gap-2" data-testid="map-playback-controls">
    <Button size="sm" onClick={() => (pb.playing ? pb.pause() : pb.play())} data-testid="map-play-button" className="w-24">
      {pb.playing ? <><Pause className="h-4 w-4 mr-1.5" />Pause</> : <><Play className="h-4 w-4 mr-1.5" />Play</>}
    </Button>
    <Button size="icon" variant="ghost" onClick={pb.restart} title="Restart from the beginning" data-testid="map-restart-button"><RotateCcw className="h-4 w-4" /></Button>
    <Select value={String(pb.speed)} onValueChange={(v) => pb.setSpeed(Number(v))}>
      <SelectTrigger className="h-9 w-36" data-testid="map-speed-select"><SelectValue /></SelectTrigger>
      <SelectContent>{SPEEDS.map((s) => <SelectItem key={s} value={String(s)}>{s} yr / sec</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const MapTimeBar = ({ timelines, time, onChange, range, onClose, pb }) => {
  const lo = Math.min(range[0], time.t), hi = Math.max(range[1], time.t);
  const set = (patch) => { pb?.pause(); onChange({ ...time, ...patch }); };
  return (
    <div className="absolute bottom-16 left-3 right-3 z-30 rounded-md border border-[#2D3442] bg-[#13171F]/90 p-3 backdrop-blur-md sm:left-auto sm:right-4 sm:w-[440px]" data-testid="map-time-bar">
      <div className="mb-2 flex items-center gap-2">
        <Clock className="h-4 w-4 shrink-0 text-[#F59E0B]" />
        <Select value={time.tid} onValueChange={(tid) => set({ tid })}>
          <SelectTrigger className="h-9 min-w-0 flex-1" data-testid="map-time-timeline-select"><SelectValue /></SelectTrigger>
          <SelectContent>{timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" className="h-9 w-24 font-mono-lf" value={Math.floor(time.t)} onChange={(e) => set({ t: parseInt(e.target.value || "0", 10) })} data-testid="map-time-year-input" />
        <button onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" aria-label="Turn off time view" data-testid="map-time-close"><X className="h-4 w-4" /></button>
      </div>
      <Slider min={lo} max={hi} step={1} value={[Math.min(hi, Math.max(lo, time.t))]} onValueChange={([t]) => set({ t })} data-testid="map-time-slider" />
      <div className="mt-1 flex justify-between font-mono-lf text-[10px] text-[#A8A090]"><span>{lo}</span><span>{hi}</span></div>
      {pb && <PlayControls pb={pb} />}
    </div>
  );
};

const StageRow = ({ s, tlName, entities, swatches, onPatch, onDelete }) => {
  const [year, setYear] = useState(s.from_t);
  const commitYear = () => { const v = parseInt(year, 10); if (!Number.isNaN(v) && v !== s.from_t) onPatch({ from_t: v }); else setYear(s.from_t); };
  return (
    <div className="space-y-2 rounded-md border border-[#2D3442] bg-[#0F131A] p-3" data-testid={`region-stage-${s.id}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#A8A090]">From year</span>
        <Input type="number" className="h-8 w-24 font-mono-lf" value={year} onChange={(e) => setYear(e.target.value)} onBlur={commitYear} data-testid={`region-stage-year-${s.id}`} />
        <span className="min-w-0 flex-1 truncate text-xs text-[#C4B5FD]">{tlName}</span>
        <button type="button" onClick={onDelete} className="text-[#A8A090] hover:text-red-400" aria-label="Delete change" data-testid={`region-stage-delete-${s.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onPatch({ hidden: !s.hidden })} data-testid={`region-stage-hidden-${s.id}`}
          className={`rounded-sm border px-2 py-1 text-xs ${s.hidden ? "border-red-400 text-red-300" : "border-[#2D3442] text-[#A8A090]"}`}>{s.hidden ? "Gone from here" : "Exists"}</button>
        {!s.hidden && (
          <Select value={s.entity_id || "none"} onValueChange={(v) => onPatch({ entity_id: v === "none" ? null : v })}>
            <SelectTrigger className="h-8 min-w-0 flex-1 text-xs" data-testid={`region-stage-owner-${s.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No owner</SelectItem>
              {entities.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      {!s.hidden && (
        <div className="flex flex-wrap gap-1.5">
          {swatches.map((c) => (
            <button type="button" key={c} onClick={() => onPatch({ color: c })} data-testid={`region-stage-color-${s.id}-${c}`}
              className={`h-5 w-5 rounded-full ${s.color === c ? "ring-2 ring-offset-2 ring-offset-[#0F131A] ring-[#F3EBDD]" : ""}`} style={{ background: c }} />
          ))}
        </div>
      )}
    </div>
  );
};

export const StageList = ({ region, timelines, entities, swatches, time, resolved, api, onSaved }) => {
  const tlName = (id) => timelines.find((t) => t.id === id)?.name || "Unknown timeline";
  const call = async (fn) => { try { await fn(); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save change"); } };
  const add = (hidden) => call(() => api.post(`/regions/${region.id}/stages`, {
    timeline_id: time.tid, from_t: time.t, points: resolved.points, color: resolved.color, entity_id: resolved.entity_id || null, hidden,
  }));
  const stages = [...(region.stages || [])].sort((a, b) => a.from_t - b.from_t);
  return (
    <div className="space-y-2" data-testid="region-stage-list">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-[#F3EBDD]">Changes over time</div>
        <span className="text-xs text-[#A8A090]">{stages.length}</span>
      </div>
      {stages.length === 0 && <p className="text-xs text-[#A8A090]">No changes yet. Turn on Time on the map, pick a year, then add a change or edit the shape.</p>}
      {stages.map((s) => (
        <StageRow key={`${s.id}-${s.from_t}`} s={s} tlName={tlName(s.timeline_id)} entities={entities} swatches={swatches}
          onPatch={(p) => call(() => api.patch(`/regions/${region.id}/stages/${s.id}`, p))}
          onDelete={() => call(() => api.delete(`/regions/${region.id}/stages/${s.id}`))} />
      ))}
      {time && (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="sm" variant="outline" onClick={() => add(false)} data-testid="region-stage-add"><Plus className="mr-1 h-3.5 w-3.5" />Change from year {time.t}</Button>
          <Button type="button" size="sm" variant="ghost" className="text-red-300" onClick={() => add(true)} data-testid="region-stage-add-gone">Gone from year {time.t}</Button>
        </div>
      )}
      {time && <p className="text-[11px] text-[#A8A090]">New changes apply to {tlName(time.tid)} and any branches that split off after year {time.t}.</p>}
    </div>
  );
};
